import { getPadProvisionalOutcome } from './conditionChecks';

/**
 * Utility function to determine if an element should be displayed based on conditions
 * specified in the data model. This works at any hierarchy level (question, subsection, section).
 * 
 * @param {Object} element - The element (question, subsection, section, etc.) with potential conditions
 * @param {Object} patientDataModel - The full patient data model
 * @returns {boolean} - Whether the element should be displayed
 */
export const ShouldDisplayElement = (element, patientDataModel) => {
  // If no conditions specified, always show the element
  if (!element.conditionsToShow) {
    return true;
  }

  // Check all conditions (every condition must pass)
  return Object.values(element.conditionsToShow).every(condition => {
    // Get the module data
    const moduleData = patientDataModel?.dataModel?.[condition.module];
    if (!moduleData) return false;

    // Find the relevant section if sectionTitle is specified
    let fieldValue;
    
    if (condition.sectionTitle) {
      // Find section with matching title
      const sectionEntry = Object.entries(moduleData.sections || {}).find(
        ([_, section]) => section.sectionTitle === condition.sectionTitle
      );
      
      if (!sectionEntry) return false;
      
      // Get the field value from the matched section
      fieldValue = sectionEntry[1][condition.field || condition.firestoreFieldName];
    } else {
      // If no section title specified, look for the field directly in sections by index
      if (condition.sectionIndex) {
        const section = moduleData.sections?.[condition.sectionIndex];
        if (section) {
          fieldValue = section[condition.field || condition.firestoreFieldName];
        }
      } else {
        // Look through all sections
        for (const sectionKey in moduleData.sections || {}) {
          const section = moduleData.sections[sectionKey];
          if ((condition.field || condition.firestoreFieldName) in section) {
            fieldValue = section[condition.field || condition.firestoreFieldName];
            break;
          }
        }
        
        // If not found in sections, check module itself
        if (fieldValue === undefined) {
          fieldValue = moduleData[condition.field || condition.firestoreFieldName];
        }
      }
    }
    
    // SLaM Path 1: PAD goals / positive-only checklists key off formal
    // diagnosisDecision. Before DD exists (and while the Path 1 provisional
    // choice stands under the soft-warning regime), honour the provisional
    // outcome so the in-appointment PAD shows the correct panels. Treat
    // null / empty-string the same as missing for this field only — unlocking
    // DD on CI finish can leave a diagnosisDecision key present but unset,
    // which would otherwise hide Patient's Goals even after a positive
    // provisional PAD. Uses the shared CONTENT resolver so this stays a
    // single source of truth — for any patient without an in-force Path-1
    // provisional this maps to null and the condition falls through to the
    // normal `undefined` handling below.
    const isDiagnosisDecisionField =
      condition.field === 'diagnosisDecision' ||
      condition.firestoreFieldName === 'diagnosisDecision';
    const diagnosisDecisionUnset =
      fieldValue === undefined || fieldValue === null || fieldValue === '';
    if (isDiagnosisDecisionField && diagnosisDecisionUnset) {
      const provisional = getPadProvisionalOutcome(patientDataModel);
      if (provisional === 'positive') {
        fieldValue = 'Fully met';
      } else if (provisional === 'negative') {
        fieldValue = 'Not met';
      }
    }

    // If field value couldn't be found, condition fails
    if (fieldValue === undefined) return false;
    
    // Check valueIsOneOf condition (if specified)
    if (condition.valueIsOneOf && !condition.valueIsOneOf.includes(fieldValue)) {
      return false;
    }
    
    // Check valueIsNotOneOf condition (if specified)
    if (condition.valueIsNotOneOf && condition.valueIsNotOneOf.includes(fieldValue)) {
      return false;
    }
    
    // All checks passed for this condition
    return true;
  });
};